import { Component, NgZone, OnInit } from '@angular/core';
import moment from 'moment';
import { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { MessService } from '../mess.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgDialogAnimationService } from 'ng-dialog-animation';
import { AddMealRequestComponent } from '../add-meal-request/add-meal-request.component';

import { MAT_DATE_FORMATS } from '@angular/material/core';

const MEAL_REQUEST_DATE_FORMATS = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};


@Component({
  selector: 'app-meal-request',
  templateUrl: './meal-request.component.html',
  styleUrls: ['./meal-request.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: MEAL_REQUEST_DATE_FORMATS }],

})
export class MealRequestComponent implements OnInit {
  // keep same date format used in other mess screens
  current = new Date();
  /** Disallow selecting past dates */
  minDate = moment().startOf('day').toDate();
  /** Allow selecting only today + 1 day (future) */
  maxDate = moment().add(1, 'day').toDate();

  selectDate: string = '';
  quickFilter: string = '';
  campusName = '';

  private gridApi?: GridApi;

  rowData: any[] = [];
  pinnedBottomRowData: any[] = [];

  defaultColDef: ColDef = {
    flex: 1,
    menuTabs: [],
    suppressHeaderMenuButton: true,
    sortable: true,
    filter: false,
    resizable: true,
    headerClass: 'meal-request-header-center',
  };

  private readonly allMealTypeCodes = ['Breakfast', 'Lunch', 'Snacks', 'Dinner'] as const;
  private readonly allScenarioIds = [13, 14, 15, 16] as const;
  private readonly scenarioIdToMealType: Record<number, string> = {
    13: 'Breakfast',
    14: 'Lunch',
    15: 'Snacks',
    16: 'Dinner',
  };

  private mealRequestEditCellRenderer = (params: any): HTMLElement | string => {
    if (params.node?.rowPinned) {
      return '';
    }
    const wrap = document.createElement('div');
    wrap.className = 'meal-request-edit-cell';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'meal-request-edit-btn';
    btn.innerText = 'Edit';
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!params.data) {
        return;
      }
      // AG Grid cellRenderer uses native DOM click — run inside Angular zone so dialog receives row data.
      this.zone.run(() => {
        this.openAddMealRequestDialog({ ...params.data });
      });
    });
    wrap.appendChild(btn);
    return wrap;
  };

  colDefs: ColDef[] = [
    {
      headerName: 'S.No',
      minWidth: 30,
      cellStyle: { 'text-align': 'center', 'font-size': '14px' },
      valueGetter: (params: any) =>
        params.node?.rowPinned ? '' : (params.node?.rowIndex ?? 0) + 1,
    },
    // {
    //   field: 'kitchenName',
    //   headerName: 'Kitchen',
    //   minWidth: 300,
    //   cellRenderer: (params: any) => {
    //     if (params.node?.rowPinned) {
    //       const bold = document.createElement('strong');
    //       bold.innerText = 'Grand Total';
    //       bold.style.fontSize = '14px';
    //       return bold;
    //     }
    //     return params.value ?? '';
    //   },
    // },
    {
      field: 'mealType',
      headerName: 'Meal Type',
      minWidth: 180,
      valueFormatter: (p) => (p.node?.rowPinned ? '' : (p.value ?? '')),
    },
    {
      field: 'studentCount',
      headerName: 'Students',
      width: 120,
      cellStyle: { 'text-align': 'right' },
      valueFormatter: (p) => this.formatCount(p.value),
    },
    {
      field: 'employeeCount',
      headerName: 'Staff',
      width: 120,
      cellStyle: { 'text-align': 'right' },
      valueFormatter: (p) => this.formatCount(p.value),
    },
    {
      field: 'subStaffCount',
      headerName: 'SubStaff',
      width: 120,
      cellStyle: { 'text-align': 'right' },
      valueFormatter: (p) => this.formatCount(p.value),
    },
    {
      field: 'visitorsCount',
      headerName: 'Others',
      width: 120,
      cellStyle: { 'text-align': 'right' },
      valueFormatter: (p) => this.formatCount(p.value),
    },
    {
      field: 'totalHeads',
      headerName: 'Total Heads',
      width: 130,
      cellStyle: { 'text-align': 'right', 'font-weight': '600' },
      valueFormatter: (p) => this.formatCount(p.value),
    },
    {
      headerName: 'Action',
      field: 'edit',
      width: 100,
      minWidth: 90,
      maxWidth: 110,
      sortable: false,
      suppressHeaderMenuButton: true,
      cellStyle: { 'text-align': 'center' },
      cellRenderer: this.mealRequestEditCellRenderer,
    },
  ];

  overlayLoadingTemplate = `
    <span class="ag-overlay-loading-center">Please wait while data is loading...</span>
  `;
  overlayNoRowsTemplate = `
    <span>No meal requests for this date yet Tab +Add Meal Request to create one</span>
  `;

  /** Used by Angular Material datepicker; kept for template compatibility. */
  daysheetDateClass = (_date: Date): string => '';
  // kitchens: any;
  // selectedSupplyId: null;
  kitchens: any[] = [];
  selectedSupplyId: number | null = null;

  get isAddMealRequestDisabled(): boolean {
    if (!this.rowData?.length) {
      return false;
    }
    const presentScenarioIds = new Set(
      this.rowData
        .map((row) => Number(row?.scenarioId))
        .filter((id) => Number.isFinite(id)),
    );
    if (this.allScenarioIds.every((id) => presentScenarioIds.has(id))) {
      return true;
    }
    const presentMealTypes = new Set(
      this.rowData
        .map((row) => this.normalizeMealTypeCode(row?.mealType ?? row?.scenarioCode))
        .filter(Boolean),
    );
    return this.allMealTypeCodes.every((code) => presentMealTypes.has(code));
  }

  constructor(
    private messService: MessService,
    private spinner: NgxSpinnerService,
    private dialog: NgDialogAnimationService,
    private zone: NgZone,
  ) { }

  ngOnInit(): void {
    this.campusName = localStorage.getItem('userName') ?? '';
    this.selectDate = moment(this.current).format('DD MMM YYYY');
    this.loadKitchens();
  }

  private loadKitchens(): void {
    this.spinner.show();
    this.messService.userKitchens().subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
        this.kitchens = rows;
        if (this.kitchens.length > 0) {
          this.selectedSupplyId = this.kitchens[0].supplyId;
        }
        console.log(this.selectedSupplyId);
        this.spinner.hide();
        this.loadFoodRequestHistory();
      },
      error: () => this.spinner.hide(),
    });
  }

  // onKitchenChange(event: any): void {

  //   this.selectedSupplyId = Number(event.target.value);

  //   console.log('Changed Supply ID:', this.selectedSupplyId);

  //   // optional → get full selected object
  //   const selectedKitchen = this.kitchens.find(
  //     k => k.supplyId === this.selectedSupplyId
  //   );
  //   this.loadFoodRequestHistory();
  //   console.log('Selected Kitchen:', selectedKitchen);
  // }

  onKitchenChange(supplyId: number): void {

    this.selectedSupplyId = supplyId;

    console.log('Changed Supply ID:', this.selectedSupplyId);

    const selectedKitchen = this.kitchens.find(
      k => k.supplyId === this.selectedSupplyId
    );

    console.log('Selected Kitchen:', selectedKitchen);

    this.loadFoodRequestHistory();
  }

  getKitchenOptionLabel(k: any): string {
    const buildingName = String(k?.buildingName ?? '').trim();
    const subInventoryCode = String(k?.subInventoryCode ?? '').trim();
    if (buildingName && subInventoryCode) {
      return `${buildingName} - ${subInventoryCode}`;
    }
    return buildingName || subInventoryCode || '-';
  }



  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    // this.loadFoodRequestHistory();
  }

  onStockDateChange(event: any): void {
    this.selectDate = moment(event?.value ?? this.current).format('DD MMM YYYY');
    this.loadFoodRequestHistory();
  }

  onfilterChange(event: any): void {
    this.quickFilter = event?.target?.value ?? '';
  }

  onMealRequestRowClicked(event: RowClickedEvent): void {
    if (!event?.data || event.node?.rowPinned) {
      return;
    }
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest?.('.meal-request-edit-btn')) {
      return;
    }
    this.openAddMealRequestDialog({ ...event.data });
  }

  addMealRequest(): void {
    if (this.isAddMealRequestDisabled) {
      return;
    }
    this.openAddMealRequestDialog();
  }

  private openAddMealRequestDialog(prefillRow?: any): void {
    const selectedKitchen = this.getSelectedKitchen();
    const editRow = prefillRow ? this.buildEditRowData(prefillRow) : undefined;

    const dialogRef = this.dialog.open(AddMealRequestComponent, {
      width: '30%',
      height: '100%',
      data: {
        date: this.selectDate,
        existingItems: this.rowData,
        supplyId: editRow ? editRow.supplyId : selectedKitchen?.supplyId,
        buildingName: editRow
          ? editRow.buildingName
          : String(selectedKitchen?.buildingName ?? '').trim(),
        subInventoryCode: editRow
          ? editRow.subInventoryCode
          : String(selectedKitchen?.subInventoryCode ?? '').trim(),
        editMode: !!editRow,
        ...(editRow ? { prefillRow: editRow } : {}),
      },
      panelClass: 'mess-inventory-right-dialog',
      position: { right: '0px' },
      animation: { to: 'left' },
      maxWidth: '100%',
      autoFocus: false,
      restoreFocus: false,
      disableClose: true,
      hasBackdrop: true,
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      // If dialog was closed after successful submit, refresh history table.
      if (res) {
        this.loadFoodRequestHistory();
      }
    });
  }

  private buildEditRowData(row: any): any {
    const selectedKitchen = this.getSelectedKitchen();
    return {
      ...row,
      supplyId: Number(row?.supplyId ?? this.selectedSupplyId ?? NaN) || this.selectedSupplyId,
      buildingName: String(
        row?.kitchenName ?? row?.buildingName ?? selectedKitchen?.buildingName ?? '',
      ).trim(),
      subInventoryCode: String(
        row?.subInventoryCode ?? selectedKitchen?.subInventoryCode ?? '',
      ).trim(),
    };
  }

  // Template button label is "Add Item" in this screen; keep binding stable.
  addItem(): void {
    this.addMealRequest();
  }

  private updatePinnedBottomRow(): void {
    if (!this.rowData?.length) {
      this.pinnedBottomRowData = [];
      return;
    }
    const sum = (field: string) =>
      this.rowData.reduce((s: number, r: any) => s + Number(r[field] ?? 0), 0);
    this.pinnedBottomRowData = [{
      kitchenName: 'Grand Total',
      mealType: '',
      studentCount: sum('studentCount'),
      employeeCount: sum('employeeCount'),
      subStaffCount: sum('subStaffCount'),
      visitorsCount: sum('visitorsCount'),
      totalHeads: sum('totalHeads'),
    }];
  }

  /** Indian digit grouping for head-count columns (same style as mess inventory). */
  private formatCount(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const n = Number(String(value).replace(/,/g, ''));
    if (isNaN(n)) {
      return '';
    }
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(n);
  }

  private loadFoodRequestHistory(): void {
    const date = this.selectDate || moment(this.current).format('DD MMM YYYY');
    if (this.selectedSupplyId == null || !Number.isFinite(Number(this.selectedSupplyId))) {
      this.rowData = [];
      this.updatePinnedBottomRow();
      this.gridApi?.showNoRowsOverlay();
      return;
    }
    this.gridApi?.showLoadingOverlay();
    this.spinner.show();
    this.messService.foodRequestHistory(this.selectedSupplyId, date).subscribe({
      next: (res: any) => {
        const rows = this.mapFoodRequestHistoryResponse(res);
        this.rowData = rows;
        this.updatePinnedBottomRow();
        this.spinner.hide();
        if (!rows?.length) {
          this.gridApi?.showNoRowsOverlay();
        } else {
          this.gridApi?.hideOverlay();
        }
      },
      error: () => {
        this.rowData = [];
        this.pinnedBottomRowData = [];
        this.gridApi?.showNoRowsOverlay();
        this.spinner.hide();
      },
    });
  }

  /** Flatten foodRequestHistory response (e.g. `{ scenarioId: 13, list: [...] }`) into grid rows. */
  private mapFoodRequestHistoryResponse(res: any): any[] {
    const raw = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : res != null && typeof res === 'object' ? [res] : []);
    const rows: any[] = [];

    for (const item of raw) {
      if (!item || typeof item !== 'object') {
        continue;
      }
      const nested =
        item.list ??
        item.foodRequestList ??
        item.foodRequests ??
        item.requests ??
        item.requestList;
      if (Array.isArray(nested) && nested.length) {
        nested.forEach((child: any) => {
          rows.push(this.normalizeFoodRequestRow(child, item));
        });
      } else {
        rows.push(this.normalizeFoodRequestRow(item));
      }
    }

    return this.sortMealRequestRows(rows);
  }

  /** Display order: Breakfast → Lunch → Snacks → Dinner. */
  private sortMealRequestRows(rows: any[]): any[] {
    return [...rows].sort((a, b) => {
      const orderA = this.getMealTypeSortOrder(a);
      const orderB = this.getMealTypeSortOrder(b);
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return String(a?.kitchenName ?? '').localeCompare(String(b?.kitchenName ?? ''));
    });
  }

  private getMealTypeSortOrder(row: any): number {
    const mealType = this.normalizeMealTypeCode(row?.mealType);
    const mealIndex = this.allMealTypeCodes.findIndex((code) => code === mealType);
    if (mealIndex >= 0) {
      return mealIndex;
    }
    const scenarioId = Number(row?.scenarioId);
    const scenarioIndex = this.allScenarioIds.findIndex((id) => id === scenarioId);
    if (scenarioIndex >= 0) {
      return scenarioIndex;
    }
    return 999;
  }

  private normalizeFoodRequestRow(row: any, parent?: any): any {
    const scenarioId = Number(row?.scenarioId ?? parent?.scenarioId ?? NaN);
    const rawMealType = String(
      row?.mealType ?? row?.scenarioCode ?? parent?.scenarioCode ?? parent?.mealType ?? '',
    ).trim();
    const mealType = this.resolveMealTypeFromScenarioId(scenarioId, rawMealType);
    const kitchenName = String(
      row?.kitchenName ?? row?.buildingName ?? row?.supplyBuildingName ?? parent?.kitchenName ?? parent?.buildingName ?? this.getSelectedKitchenName() ?? '',
    ).trim();
    const studentCount = Number(row?.studentCount ?? 0) || 0;
    const employeeCount = Number(row?.employeeCount ?? 0) || 0;
    const subStaffCount = Number(row?.subStaffCount ?? 0) || 0;
    const visitorsCount = Number(row?.visitorsCount ?? 0) || 0;
    const totalHeads = Number(row?.totalHeads ?? studentCount + employeeCount + subStaffCount + visitorsCount) || 0;

    return {
      ...row,
      scenarioId: Number.isFinite(scenarioId) ? scenarioId : row?.scenarioId,
      mealType,
      kitchenName,
      supplyId: Number(row?.supplyId ?? parent?.supplyId ?? this.selectedSupplyId ?? NaN) || this.selectedSupplyId,
      subInventoryCode: String(
        row?.subInventoryCode ?? parent?.subInventoryCode ?? this.getSelectedKitchen()?.subInventoryCode ?? '',
      ).trim(),
      studentCount,
      employeeCount,
      subStaffCount,
      visitorsCount,
      totalHeads,
    };
  }

  private normalizeMealTypeCode(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
  }

  private resolveMealTypeFromScenarioId(scenarioId: number, fallback: string): string {
    if (Number.isFinite(scenarioId) && this.scenarioIdToMealType[scenarioId]) {
      return this.scenarioIdToMealType[scenarioId];
    }
    const code = this.normalizeMealTypeCode(fallback);
    return code || fallback;
  }

  private getSelectedKitchenName(): string {
    return String(this.getSelectedKitchen()?.buildingName ?? '').trim();
  }

  private getSelectedKitchen(): any {
    return this.kitchens.find((k) => Number(k?.supplyId) === Number(this.selectedSupplyId)) ?? null;
  }


}
